import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles, categories } from "@/mocks/articles";
import { useState } from "react";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [sortBy, setSortBy] = useState<"latest" | "hot">("latest");

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryArticles = articles
    .filter((a) => a.category === slug)
    .sort((a, b) => {
      if (sortBy === "hot") return b.views - a.views;
      return new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
    });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={[
              { label: "首页", href: "/" },
              { label: category.name },
            ]} />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <span className="w-5 h-5 flex items-center justify-center text-primary-600"><i className={`${category.icon} text-lg`}></i></span>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">共 {categoryArticles.length} 篇相关爆料</p>
              </div>
            </div>
            <div className="flex items-center gap-1 bg-background-200 rounded-full p-1">
              <button
                onClick={() => setSortBy("latest")}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === "latest" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                最新
              </button>
              <button
                onClick={() => setSortBy("hot")}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === "hot" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                最热
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200 transition-all duration-200 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {article.is_hot && (
                    <div className="absolute top-3 right-3">
                      <span className="w-7 h-7 rounded-full bg-accent-500 text-background-50 flex items-center justify-center">
                        <i className="ri-fire-line text-sm"></i>
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-snug font-heading mb-3">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.published_at}</span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center"><i className="ri-eye-line"></i></span>
                      {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂时没有内容</p>
              <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">返回首页</Link>
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-2">
            <span className="text-sm text-foreground-500 mr-2 flex items-center">其他分类：</span>
            {categories.filter((c) => c.slug !== slug).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-3 py-1.5 rounded-full bg-background-200 text-foreground-600 text-xs hover:bg-primary-100 hover:text-primary-700 transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <span className="w-3 h-3 flex items-center justify-center"><i className={cat.icon}></i></span>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}